import { isPicklistFilter, isPicklistLogicalOperator } from "../../../../Engine/Core/Picklists/PicklistTypes";
import {
    DigestFilter,
    DigestFilterOperator,
    DigestLogicalOperator,
} from "../../../../../../Common/CandyApiClient/DigestApiClient/Types/DigestQueryFilter";
import { isNullOrEmpty } from "../../../../../../Common/TypingUtils";

export interface IFilterBuilder {
    readonly and: FilterBuilder;
    readonly or: FilterBuilder;
    column(name: string): void;
    eql(value: string): void;
    eqlMultiple(value: string): void;
    in(value: string[]): void;
    build(): DigestFilter | undefined;
}

export interface ResolveToNoneFilter {
    type: "ResolveToNoneFilter";
}
export const resolveToNoneFilter: ResolveToNoneFilter = { type: "ResolveToNoneFilter" };

export interface ResolveToAllFilter {
    type: "ResolveToAllFilter";
}
export const resolveToAllFilter: ResolveToAllFilter = { type: "ResolveToAllFilter" };

export function isResolveToAllFilter(
    value: FilterBuilder | ResolveToAllFilter | ResolveToNoneFilter
): value is ResolveToAllFilter {
    return "type" in value && value.type === "ResolveToAllFilter";
}

export function isResolveToNoneFilter(
    value: FilterBuilder | ResolveToAllFilter | ResolveToNoneFilter
): value is ResolveToNoneFilter {
    return "type" in value && value.type === "ResolveToNoneFilter";
}

export class FilterBuilder implements IFilterBuilder {
    private readonly output: Array<DigestFilter | DigestLogicalOperator>;
    private readonly stack: DigestLogicalOperator[];

    private tempColumnName: string;
    private tempValueOperand: string;

    public constructor() {
        this.output = [];
        this.stack = [];

        this.tempColumnName = "";
        this.tempValueOperand = "";
    }

    public column = (name: string) => {
        this.tempColumnName = name;

        return this;
    };

    public eql = (value: string) => {
        this.tempValueOperand = value;

        this.addOperandToStack(DigestFilterOperator.Equal);
        this.clearTempDataOperand();

        return this;
    };

    public eqlMultiple = (value: string) => {
        if (isNullOrEmpty(value)) {
            return this.eql(value);
        }
        try {
            const array: string[] = JSON.parse(value);
            if (array.length === 0) {
                return this.eql(value);
            }
            array.forEach((item, index) => {
                this.tempValueOperand = item;
                this.addOperandToStack(DigestFilterOperator.Equal);
                if (index !== array.length - 1) {
                    this.stack.push(DigestLogicalOperator.Or);
                }
            });
        } catch {
            return this.eql(value);
        }
        this.clearTempDataOperand();
        return this;
    };

    public contains = (value: string) => {
        this.tempValueOperand = value;

        this.addOperandToStack(DigestFilterOperator.Substring);
        this.clearTempDataOperand();

        return this;
    };

    public includes = (value: string) => {
        this.tempValueOperand = value;

        this.addOperandToStack(DigestFilterOperator.ContainsInList);
        this.clearTempDataOperand();

        return this;
    };

    public startWith = (value: string) => {
        this.tempValueOperand = value;

        this.addOperandToStack(DigestFilterOperator.Prefix);
        this.clearTempDataOperand();

        return this;
    };

    public in = (value: string[]) => {
        if (!Array.isArray(value)) {
            throw new Error(`Expected array, got ${typeof value}`);
        }

        const filter = this.formFilterFromArray(value);

        this.output.push(filter);
        this.clearTempDataOperand();

        return this;
    };

    public get and(): FilterBuilder {
        const stack = this.stack;
        const output = this.output;
        const lastOperator = stack[stack.length - 1];

        if (lastOperator === DigestLogicalOperator.Or) {
            stack.push(DigestLogicalOperator.And);
        } else if (lastOperator === DigestLogicalOperator.And) {
            output.push(DigestLogicalOperator.And);
        } else {
            stack.push(DigestLogicalOperator.And);
        }

        return this;
    }

    public get or(): FilterBuilder {
        const stack = this.stack;
        const output = this.output;
        const lastOperator = stack[stack.length - 1];

        if (lastOperator === DigestLogicalOperator.And) {
            const lastElement = stack.pop();

            if (lastElement !== undefined) {
                output.push(lastElement);
            }

            stack.push(DigestLogicalOperator.Or);
        } else if (lastOperator === DigestLogicalOperator.Or) {
            output.push(DigestLogicalOperator.Or);
        } else {
            stack.push(DigestLogicalOperator.Or);
        }

        return this;
    }

    public build(): DigestFilter | undefined {
        const output = [...this.output, ...this.stack.reverse()];
        const buildResult = this.buildFilter(output)[0];

        return buildResult != undefined && isPicklistFilter(buildResult) ? buildResult : undefined;
    }

    private buildFilter(
        output: Array<DigestFilter | DigestLogicalOperator>,
        index: number = 0
    ): Array<DigestFilter | DigestLogicalOperator> {
        const operator = output[index];

        if (operator != undefined && isPicklistLogicalOperator(operator)) {
            const operands = output.slice(index - 2, index).filter(isPicklistFilter);
            const items: DigestFilter = {
                operator: operator,
                operands: operands,
            };

            output.splice(index - 2, 3, items);

            return this.buildFilter(output, index - 1);
        }

        return output.length === 1 ? output : this.buildFilter(output, index + 1);
    }

    private addOperandToStack(operator: DigestFilterOperator): void {
        this.output.push({
            operator: operator,
            column: this.tempColumnName,
            value: this.tempValueOperand,
        });
    }

    private clearTempDataOperand(): void {
        this.tempColumnName = "";
        this.tempValueOperand = "";
    }

    private formFilterFromArray(value: string[]): DigestFilter {
        return {
            operator: DigestLogicalOperator.Or,
            operands: value.map(item => ({
                operator: DigestFilterOperator.Equal,
                column: this.tempColumnName,
                value: item,
            })),
        };
    }
}
